import { useEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { cn } from "../../utils/cn";

export interface DropdownMenuItem {
  key: string;
  label: ReactNode;
  icon?: string;
  onClick?: () => void;
  disabled?: boolean;
  danger?: boolean;
  divider?: boolean;
}

interface DropdownProps {
  trigger: (open: boolean) => ReactNode;
  items: DropdownMenuItem[];
  align?: "left" | "right";
  width?: string;
  placement?: "auto" | "top" | "bottom";
}

interface PositionCoords {
  top: number;
  left: number;
  dropUp: boolean;
}

export function Dropdown({ trigger, items, align = "right", width = "w-56", placement = "auto" }: DropdownProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<PositionCoords | null>(null);

  useEffect(() => {
    if (!open) return;

    function handleOutsideClick(event: MouseEvent | TouchEvent) {
      const target = event.target as Node | null;
      if (
        rootRef.current &&
        !rootRef.current.contains(target) &&
        menuRef.current &&
        !menuRef.current.contains(target)
      ) {
        setOpen(false);
      }
    }

    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("touchstart", handleOutsideClick);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("touchstart", handleOutsideClick);
    };
  }, [open]);

  const calculatePosition = () => {
    if (!rootRef.current) return;
    const rect = rootRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const estimatedHeight = Math.min(items.length * 36 + 16, 280);

    const isUp = placement === "top" || (placement === "auto" && spaceBelow < estimatedHeight && spaceAbove > spaceBelow);

    setCoords({
      top: isUp ? rect.top : rect.bottom,
      left: align === "right" ? rect.right : rect.left,
      dropUp: isUp,
    });
  };

  useEffect(() => {
    if (!open) return;
    calculatePosition();

    const handleScrollOrResize = () => {
      calculatePosition();
    };

    window.addEventListener("scroll", handleScrollOrResize, true);
    window.addEventListener("resize", handleScrollOrResize);
    return () => {
      window.removeEventListener("scroll", handleScrollOrResize, true);
      window.removeEventListener("resize", handleScrollOrResize);
    };
  }, [open, placement, align, items.length]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div ref={rootRef} className="relative inline-block" onKeyDown={handleKeyDown}>
      <div
        onClick={() => {
          if (!open) calculatePosition();
          setOpen((v) => !v);
        }}
      >
        {trigger(open)}
      </div>
      {open && coords
        ? createPortal(
            <div
              ref={menuRef}
              className={cn(
                "fixed z-[99999] min-w-max max-w-xs sm:max-w-md overflow-hidden rounded-md border border-background-200 bg-background-50 py-1 shadow-xl shadow-foreground-950/20",
                width,
                coords.dropUp ? "-translate-y-full mb-1" : "mt-1",
                align === "right" ? "-translate-x-full" : ""
              )}
              style={{
                top: `${coords.top}px`,
                left: `${coords.left}px`,
              }}
            >
              {items.map((item) =>
                item.divider ? (
                  <div key={item.key} className="my-1 border-t border-background-100" />
                ) : (
                  <button
                    key={item.key}
                    type="button"
                    disabled={item.disabled}
                    onClick={() => {
                      setOpen(false);
                      item.onClick?.();
                    }}
                    className={cn(
                      "flex w-full cursor-pointer items-center gap-2.5 px-3 py-2 text-left text-sm whitespace-nowrap transition-colors hover:bg-background-100 disabled:cursor-not-allowed disabled:text-foreground-400",
                      item.danger ? "text-danger-600" : "text-foreground-800"
                    )}
                  >
                    {item.icon ? <i className={cn(item.icon, "text-base flex-none")} /> : null}
                    <span className="flex-1 whitespace-nowrap font-normal">{item.label}</span>
                  </button>
                )
              )}
            </div>,
            document.body
          )
        : null}
    </div>
  );
}